import React, {useState} from 'react';
import {createPaginationContainer, graphql, RelayPaginationProp} from 'react-relay';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import ClientsListItem, {ClientRowData} from './ClientsListItem';
import {useInfiniteScroll} from '../shared/hooks/useInfiniteScroll';
import {MODAL_TYPE, showModal} from '../shared/components/modals/generic_modal_conductor';
import {CLIENT_PERMISSION_GROUPS, CLIENT_PERMISSIONS_WITH_GUEST_USER} from '../admin-tab/clients-page/ClientPermissionGroups';
import {PERMISSION_TYPE} from '../../Permissions';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {createToast} from '../shared/components/toasts/toast';
import CreateClientModal from './CreateClientModal';
import CreateContactConfirmModal from './CreateContactConfirmModal';

// ---------------------------------------------------------------------------
// Styled layout — values match the design reference image
// ---------------------------------------------------------------------------

const PageWrapper = Styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 32px 16px 32px;
`;

const HeadingRow = Styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
`;

const Heading = Styled.h1`
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  margin: 0;
  color: #232334;
`;

const ButtonGroup = Styled.div`
  display: flex;
  gap: 8px;
`;

// Secondary button — replace with design-system Button component when confirmed available
const SecondaryButton = Styled.button`
  height: 44px;
  padding: 0 12px;
  border: 1px solid #6b6d7b;
  border-radius: 8px;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #232334;
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const Description = Styled.p`
  font-size: 14px;
  line-height: 20px;
  color: #6b6d7b;
  margin: 0;
`;

const ClientsTable = Styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const TableHead = Styled.thead`
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 1;
  border-bottom: 1px solid #e5e5e5;
`;

const Th = Styled.th`
  padding: 8px 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 500;
  color: #6b6d7b;
`;

const ThRight = Styled(Th)`
  text-align: right;
`;

const ScrollContainer = Styled.div`
  overflow-y: auto;
  max-height: calc(100vh - 280px);
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ModalState = {type: 'none'} | {type: 'createClient'} | {type: 'createContactConfirm'; clientName: string};

type Profile = {
	id: string;
	name: string;
	permissions: string[];
};

type Props = {
	clients: ClientRowData[];
	companyId: string;
	clientPermissionsProfile: Profile | null;
	hasNextPage: boolean;
	onLoadMore: () => void;
};

export const ClientsPageComponent: React.FC<Props> = ({
	clients,
	companyId,
	clientPermissionsProfile,
	hasNextPage,
	onLoadMore,
}) => {
	const {formatMessage} = useIntl();
	const {handleScroll} = useInfiniteScroll({hasNextPage, onLoadMore});
	const [modalState, setModalState] = useState<ModalState>({type: 'none'});

	const handleClientPermissions = () => {
		if (!clientPermissionsProfile) {
			showModal({type: MODAL_TYPE.MUTATION_ERROR});
			return;
		}
		showModal({
			type: hasFeatureFlag('guest_user_limited_task_access') ? MODAL_TYPE.CLIENT_PERMISSIONS : MODAL_TYPE.PROFILE,
			profile: clientPermissionsProfile,
			permissions: hasFeatureFlag('guest_user_limited_task_access')
				? CLIENT_PERMISSIONS_WITH_GUEST_USER
				: CLIENT_PERMISSION_GROUPS,
			profileNameCanBeEdited: false,
			profileCanBeEdited: true,
			headerText: formatMessage({id: 'settings.edit_client_permissions'}),
			hideProfileName: true,
			description: formatMessage({id: 'client_page.client_description'}),
		});
	};

	const handleAddClient = () => {
		if (hasFeatureFlag('new_clients_feature')) {
			setModalState({type: 'createClient'});
		} else {
			showModal({type: MODAL_TYPE.ADD_CLIENT});
		}
	};

	const handleClientCreated = (_clientId: string, clientName: string) => {
		createToast({
			duration: 5000,
			message: formatMessage({id: 'clients_page.create_client.success_snackbar'}, {name: clientName}),
		});
		setModalState({type: 'createContactConfirm', clientName});
	};

	const existingClientNames = clients.map(c => c.name);

	return (
		<PageWrapper data-cy="clients-page">
			<HeadingRow>
				<Heading>{formatMessage({id: 'clients_page.heading'})}</Heading>
				<ButtonGroup>
					<SecondaryButton type="button" data-cy="edit-client-permissions-button" onClick={handleClientPermissions}>
						{formatMessage({id: 'clients_page.client_permissions'})}
					</SecondaryButton>
					<SecondaryButton type="button" data-cy="add-client-button" onClick={handleAddClient}>
						{formatMessage({id: 'clients_page.add_client'})}
					</SecondaryButton>
				</ButtonGroup>
			</HeadingRow>

			<Description>{formatMessage({id: 'clients_page.description'})}</Description>

			<ScrollContainer onScroll={handleScroll}>
				<ClientsTable>
					<TableHead>
						<tr>
							<Th style={{width: '25%'}}>{formatMessage({id: 'clients_page.column.name'})}</Th>
							<Th>{formatMessage({id: 'clients_page.column.notes'})}</Th>
							<ThRight style={{width: '100px'}}>{formatMessage({id: 'clients_page.column.projects'})}</ThRight>
							<Th style={{width: '80px'}} />
						</tr>
					</TableHead>
					<tbody>
						{clients.map(client => (
							<ClientsListItem key={client.id} client={client} />
						))}
					</tbody>
				</ClientsTable>
			</ScrollContainer>

			{modalState.type === 'createClient' && (
				<CreateClientModal
					companyId={companyId}
					existingClientNames={existingClientNames}
					onClose={() => setModalState({type: 'none'})}
					onClientCreated={handleClientCreated}
				/>
			)}

			{modalState.type === 'createContactConfirm' && (
				<CreateContactConfirmModal
					clientName={modalState.clientName}
					onAddContacts={() => setModalState({type: 'none'})} // TODO IGNITE-contacts: wire to create-contact flow when available
					onSkip={() => setModalState({type: 'none'})}
				/>
			)}
		</PageWrapper>
	);
};

// ---------------------------------------------------------------------------
// Relay wiring — maps viewer.company.clients/profiles to component props
// ---------------------------------------------------------------------------

type ClientNode = {
	id: string;
	name: string;
	notes: string | null;
	projectCount: number | null;
	logoId: string | null;
	hubspotCompanyId: string | null;
};

const PAGE_SIZE = 50;

type ViewerProps = {
	viewer: {
		company: {
			id: string;
			clients: {
				edges: Array<{node: ClientNode}>;
				pageInfo: {hasNextPage: boolean};
			};
			profiles: {
				edges: Array<{node: Profile}>;
			};
		};
	};
	relay: RelayPaginationProp;
};

const ClientsPageRelay: React.FC<ViewerProps> = ({viewer, relay}) => {
	const companyId = viewer.company?.id ?? '';
	const clients: ClientRowData[] = (viewer.company?.clients?.edges ?? [])
		.filter((edge): edge is {node: ClientNode} => edge?.node != null)
		.map(({node}) => ({
			id: node.id,
			name: node.name,
			notes: node.notes ?? null,
			projectCount: node.projectCount ?? 0,
			logoId: node.logoId ?? null,
			hubspotCompanyId: node.hubspotCompanyId ?? null,
			companyId,
		}));
	const clientPermissionsProfile =
		(viewer.company?.profiles?.edges ?? [])
			.map(e => e.node)
			.find(p => p.permissions.includes(PERMISSION_TYPE.IS_CLIENT_USER)) ?? null;
	return (
		<ClientsPageComponent
			clients={clients}
			companyId={companyId}
			clientPermissionsProfile={clientPermissionsProfile}
			hasNextPage={relay.hasMore()}
			onLoadMore={() => {
				if (!relay.isLoading()) {
					relay.loadMore(PAGE_SIZE);
				}
			}}
		/>
	);
};

export const ClientsPageQuery = graphql`
	query ClientsPage_Query {
		viewer {
			actualPersonId
			...ClientsPage_viewer
		}
	}
`;

export default createPaginationContainer(
	ClientsPageRelay,
	{
		viewer: graphql`
			fragment ClientsPage_viewer on Viewer
			@argumentDefinitions(count: {type: "Int", defaultValue: 50}, cursor: {type: "String"}) {
				id
				company {
					id
					clients(first: $count, after: $cursor) @connection(key: "Company_clients", filters: []) {
						edges {
							node {
								id
								name
								notes
								logoId
								projectCount
								hubspotCompanyId
							}
						}
						pageInfo {
							hasNextPage
						}
					}
					profiles(first: 10000) {
						edges {
							node {
								id
								name
								permissions
							}
						}
					}
				}
			}
		`,
	},
	{
		direction: 'forward',
		getConnectionFromProps: props => props.viewer?.company?.clients,
		getVariables: (_props, {count, cursor}) => ({count, cursor}),
		query: graphql`
			query ClientsPage_PaginationQuery($count: Int!, $cursor: String) {
				viewer {
					...ClientsPage_viewer @arguments(count: $count, cursor: $cursor)
				}
			}
		`,
	}
);
